import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import panelStyles from '../panelStyles';
import {formatMoney} from '../panelMetrics';
import anchorRect from '../tooltipPosition';

/**
 * Secili donemin ozeti: ciro basta, digerleri izgarada.
 * Alti metrigi esit agirlikta sıralamak yerine ciro basligi ustleniyor;
 * ADR / konaklama / pencere / iptal onu aciklayan dort kutuya dagiliyor.
 *
 * partial: donem henuz kapanmadi (icinde bulunulan ay).
 */
export default function PeriodSummary({summary, periodLabel, partial}) {
    const {t} = useTranslation();
    const classes = panelStyles();
    // Terimi soyleyen etiket anlami tasimiyor; aciklama ipucta duruyor.
    // Ekrana sabit konumlanir: kart overflow:hidden, ic kutu kirpilirdi.
    const [tip, setTip] = useState(null);

    const showHint = (event, title, body) => {
        const box = anchorRect(event.currentTarget);
        setTip({x: box.centerX, y: box.top, limit: box.viewportWidth, title, body});
    };

    const money = (primary) => (primary && primary.code
        ? `${formatMoney(primary.amount)} ${primary.code}`
        : '—');

    // Ciro on rezervasyonu icermez, adet icerir. Ikisi tutmayinca kullanici
    // "eksik mi" diye dusunuyor; sebebini rakamin altina yaziyoruz.
    const revenueNote = [
        summary.revenue && summary.revenue.others && summary.revenue.others.length
            ? summary.revenue.others.map((o) => `+ ${formatMoney(o.amount)} ${o.code}`).join(' · ')
            : null,
        summary.pendingCount > 0
            ? t('enw-panel-pending_excluded', {
                defaultValue: '{{count}} ön rezervasyon hariç',
                count: summary.pendingCount,
            })
            : null,
    ].filter(Boolean).join(' · ');

    // Kutular ayni izgarada ama ayni kumeden beslenmiyor: ciro/ADR/oda gecesi
    // on rezervasyonu da disliyor, konaklama ve pencere dislamiyor. Hangi kaydin
    // sayildigi alt satirda yaziyor — sorulmadan gorunsun diye ipucunda degil.
    const EXCL_CANCEL = t('enw-panel-excl_cancel', 'iptaller hariç');
    const EXCL_BOTH = t('enw-panel-excl_both', 'iptal ve ön rezervasyon hariç');

    const box = (label, value, sub, tone, hint) => (
        <div className={classes.sumBox}>
            <div className={classes.sumBoxLabel}>
                {hint ? (
                    <span
                        className={classes.hintAnchor}
                        onMouseEnter={(e) => showHint(e, label, hint)}
                        onMouseLeave={() => setTip(null)}
                    >
                        {label}
                        <i className="fas fa-info-circle" />
                    </span>
                ) : label}
            </div>
            <div className={classes.sumBoxValue} style={tone ? {color: tone} : undefined}>{value}</div>
            {sub && <div className={classes.sumBoxSub}>{sub}</div>}
        </div>
    );

    return (
        <section className={classes.card}>
            <header className={classes.cardHead}>
                <div>
                    <h2 className={classes.cardTitle}>
                        {t('enw-panel-period_title', {defaultValue: '{{period}} Özeti', period: periodLabel})}
                    </h2>
                    <p className={classes.cardDesc}>
                        {partial
                            ? t('enw-panel-period_desc', 'Ayın başından bugüne')
                            : t('enw-panel-period_desc_full', 'Ayın tamamı')}
                    </p>
                </div>
            </header>

            <div className={classes.cardBody}>
                <div className={classes.sumHero}>
                    <div>
                        <div className={classes.sumHeroLabel}>{t('enw-panel-revenue', 'Ciro')}</div>
                        <div className={classes.sumHeroValue}>{money(summary.revenue)}</div>
                        {revenueNote && <div className={classes.sumBoxSub}>{revenueNote}</div>}
                    </div>
                    <div className={classes.sumHeroSide}>
                        <div className={classes.sumBoxLabel}>{t('enw-panel-bookings', 'Rezervasyon')}</div>
                        <div className={classes.sumHeroCount}>{summary.count}</div>
                        <div className={classes.sumBoxSub}>{t('enw-panel-bookings_hint', 'İptaller hariç')}</div>
                    </div>
                </div>

                <div className={classes.sumGrid}>
                    {box(
                        t('enw-panel-adr', 'Ortalama gecelik fiyat'),
                        summary.adr !== null
                            ? `${formatMoney(summary.adr)} ${summary.revenue.code || ''}`.trim()
                            : '—',
                        `${t('enw-panel-adr_hint', 'Oda gecesi başına')} · ${EXCL_BOTH}`
                    )}
                    {box(
                        t('enw-panel-room_nights', 'Satılan oda gecesi'),
                        summary.roomNights > 0 ? summary.roomNights : '—',
                        summary.roomNights > 0 ? `${t('enw-panel-nights', 'Gece')} · ${EXCL_BOTH}` : null
                    )}
                    {box(
                        t('enw-panel-avg_stay', 'Ortalama konaklama'),
                        summary.avgStay !== null ? summary.avgStay.toFixed(1).replace('.', ',') : '—',
                        summary.avgStay !== null ? `${t('enw-panel-nights', 'Gece')} · ${EXCL_CANCEL}` : null
                    )}
                    {box(
                        t('enw-panel-window', 'Ne kadar önceden'),
                        summary.bookingWindow !== null ? summary.bookingWindow : '—',
                        summary.bookingWindow !== null ? `${t('enw-panel-days', 'Gün')} · ${EXCL_CANCEL}` : null,
                        undefined,
                        t('enw-panel-window_hint', 'Misafirler girişten ortalama kaç gün önce rezervasyon yapıyor. Yüksek değer erken satış, düşük değer son dakika satış demek.')
                    )}
                    {box(
                        t('enw-panel-cancellations', 'İptal'),
                        `%${Math.round(summary.cancelRate)}`,
                        summary.cancelledCount > 0
                            ? `−${money(summary.cancelledRevenue)} · ${summary.cancelledCount} kayıt`
                            : t('enw-panel-no_cancellation', 'İptal yok'),
                        summary.cancelledCount > 0 ? '#a86200' : undefined
                    )}
                    {/* No-show iptalden ayri: oda satildi, para alindi, misafir gelmedi.
                        Bu yuzden ciroda duruyor ama burada ayri sayiliyor. */}
                    {box(
                        t('enw-panel-no_show', 'No-show'),
                        summary.noShowCount,
                        summary.noShowCount > 0
                            ? money(summary.noShowRevenue)
                            : t('enw-panel-no_show_none', 'Gelmeyen misafir yok'),
                        summary.noShowCount > 0 ? '#a86200' : undefined,
                        t('enw-panel-no_show_hint', 'Rezervasyon yapıp otele hiç gelmeyen misafir. İptal değil: oda satıldı, para alındı, misafir gelmedi. Bu yüzden ciroda duruyor, iptal oranına girmiyor.')
                    )}
                </div>
            </div>

            {tip && (
                <div
                    className={classes.calTip}
                    style={{
                        left: Math.min(Math.max(tip.x, 110), tip.limit - 110),
                        top: tip.y - 10,
                    }}
                >
                    <b>{tip.title}</b>
                    <span>{tip.body}</span>
                </div>
            )}
        </section>
    );
}
